/**
 * The two-step start a new account goes through: link an agent login, then
 * connect a code source, then start a first session. Every session needs
 * the agent login; only code work needs the source (documents and
 * presentations run without a project), so the login comes first and the
 * source can be skipped on the way to a first document (FW-41). Pure, so which
 * steps a viewer sees, when a step is done and when the flow still has
 * something to offer are unit-tested without the React Query hooks.
 *
 * Only the two prerequisites of a first session are steps. Branding, team
 * and billing stay in Admin and in the setup checklist.
 */

export type FlowStepKey = "source" | "agent";

export type FlowInput = {
  /** The viewer may add code sources to the organisation (the server's
   *  ``require_org_manager``: an organisation admin or manager, or a
   *  superadmin outside it). A member who can't is never shown the step. */
  canManageSources: boolean;
  /** The organisation has a code source. The same fact as the setup
   *  checklist's "Connect a code source" row. */
  hasSource: boolean;
  /** The viewer can run an agent (``useProviderCredentials().any``): agent
   *  logins are personal, so this is the viewer's own answer. */
  hasCredential: boolean;
  /** Steps the viewer chose to skip, remembered per organisation. */
  skipped: readonly FlowStepKey[];
  /** The viewer finished the flow ("Start your first session"). */
  finished: boolean;
};

export type FlowStep = {
  key: FlowStepKey;
  done: boolean;
  /** Skipped and still not done. */
  skipped: boolean;
};

/** Never empty: everyone has the agent step. */
export type FlowSteps = [FlowStep, ...FlowStep[]];

export function buildOnboardingSteps(input: FlowInput): FlowSteps {
  const step = (key: FlowStepKey, done: boolean): FlowStep => ({
    key,
    done,
    skipped: !done && input.skipped.includes(key),
  });
  const agent = step("agent", input.hasCredential);
  return input.canManageSources ? [agent, step("source", input.hasSource)] : [agent];
}

/** Whether the flow still has something for the viewer to do: it isn't
 *  finished and a step they can see is neither done nor skipped. Someone
 *  whose steps are all done never sees the flow on their own. */
export function flowPending(steps: readonly FlowStep[], finished: boolean): boolean {
  return !finished && steps.some((s) => !s.done && !s.skipped);
}

/** The step the flow opens on: the first open one, else the first. */
export function firstOpenStep(steps: FlowSteps): FlowStepKey {
  return (steps.find((s) => !s.done && !s.skipped) ?? steps[0]).key;
}
